import React, { useCallback, useRef, useState } from 'react'
import { IntegrationEmail } from '../../../models/Email'
import { ExternalUserServiceIntegration, User } from '../../../models/User'
import { useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { formatDate, getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { useOutsideClick } from '../../../common/useOutsideClick'
import { Overlay } from 'react-bootstrap'

export interface EmailResponseRecipientsProps {
  email: IntegrationEmail
  contact: User
  emailIntegration: ExternalUserServiceIntegration
}

export const EmailResponseRecipients: React.FC<EmailResponseRecipientsProps> = ({ email, contact, emailIntegration }) => {
  const [showRecipients, setShowRecipients] = useState<boolean>(false)
  const ref = useRef<HTMLSpanElement>(null)

  const hideTooltip = useCallback(() => setShowRecipients(false), [])

  useOutsideClick(ref, hideTooltip)

  const toggleRecipientsTooltip = () => {
    setShowRecipients((v) => !v)
  }

  return (
    <>
      <div className='d-flex gap-2 align-items-center mt-2'>
        <div className='d-flex zocks-pl-2.0 paragraph-medium-extended zocks-text-gray-600'>to {getUserDisplayName(contact)}</div>
        <i ref={ref} className='bi bi-chevron-expand cursor-pointer' onClick={toggleRecipientsTooltip} />
      </div>
      <EmailRecipientsTooltip show={showRecipients} target={ref.current} email={email} contact={contact} emailIntegration={emailIntegration} />
    </>
  )
}

interface EmailRecipientsTooltipProps {
  show: boolean
  target: HTMLSpanElement | null
  email: IntegrationEmail
  contact: User
  emailIntegration: ExternalUserServiceIntegration
}

const EmailRecipientsTooltip: React.FC<EmailRecipientsTooltipProps> = ({ show, target, email, contact, emailIntegration }) => {
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)
  return (
    <Overlay target={target} show={show} placement='bottom' offset={[0, 10]}>
      {({ placement, arrowProps, show, popper, hasDoneInitialMeasure, ...props }) => (
        <div {...props} className='email-recipients-tooltip zocks-border-0.5 px-3 zocks-py-1.25 ignore-in-pdf'>
          <div className='d-flex flex-column gap-2'>
            <div className='d-flex gap-2 align-items-center'>
              <div className='email-recipients-tooltip-line d-flex justify-content-end pe-2 paragraph-medium-extended zocks-text-gray-600'>from:</div>
              <div className='d-flex gap-1 align-items-center'>
                <div className='bold'>{getUserDisplayName(currentUser)}</div>
                <div className='paragraph-medium-extended zocks-text-gray-600'>{'<' + emailIntegration.email + '>'}</div>
              </div>
            </div>
            <div className='d-flex gap-2 align-items-center'>
              <div className='email-recipients-tooltip-line d-flex justify-content-end pe-2 paragraph-medium-extended zocks-text-gray-600'>to:</div>
              <div className='d-flex gap-1 align-items-center'>
                <div className='bold'>{getUserDisplayName(contact)}</div>
                <div className='paragraph-medium-extended zocks-text-gray-600'>{'<' + contact.email + '>'}</div>
              </div>
            </div>
            <div className='d-flex flex-column p-3 gap-2 zocks-border-0.5 border zocks-border-color-gray-500'>
              <div className='d-flex align-items-center py-2 px-3 gap-3 zocks-border-0.5 zocks-bg-color-primary-light'>
                <i className='bi bi-info-square zocks-text-primary' />
                <div className='paragraph-medium-extended'>
                  AI Assistant replies are sent as Reply To, Zocks generates the reply only for the original sender
                </div>
              </div>
              {email.ccRecipients.length > 0 ? (
                <div className='d-flex gap-2 align-items-start'>
                  <div className='email-recipients-tooltip-line d-flex justify-content-end pe-2 paragraph-medium-extended zocks-text-gray-600'>cc:</div>
                  <div className='d-flex flex-column gap-2'>
                    {email.ccRecipients.map((ccRecipient) => {
                      return (
                        <div key={ccRecipient.email} className='d-flex gap-1 align-items-center'>
                          <div className='bold zocks-text-gray-600'>{ccRecipient.name || 'Unknown'}</div>
                          <div className='paragraph-medium-extended zocks-text-gray-600'>{'<' + ccRecipient.email + '>'}</div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ) : null}
            </div>
            <div className='d-flex gap-2 align-items-center'>
              <div className='email-recipients-tooltip-line d-flex justify-content-end pe-2 paragraph-medium-extended zocks-text-gray-600'>date:</div>
              <div className='d-flex gap-1 align-items-center'>
                <div className='bold'>{formatDate(email.createdAt)}</div>
              </div>
            </div>
            <div className='d-flex gap-2 align-items-center'>
              <div className='email-recipients-tooltip-line d-flex justify-content-end pe-2 paragraph-medium-extended zocks-text-gray-600'>subject:</div>
              <div className='d-flex gap-1 align-items-center'>
                <div className='bold'>{email.subject}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Overlay>
  )
}
